<template>
  <v-card
    class="model-desktop-card"
    color="background"
    variant="elevated"
    elevation="4"
    @click="emitEdit"
  >
    <div class="model-shell">
      <div class="model-header">
        <div class="model-header-main">
          <div class="model-provider-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${config.providerKey} icon`"
              class="model-provider-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="model-provider-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${config.providerKey} icon`"
            ></span>
            <span v-else class="model-provider-avatar-fallback" :style="{ backgroundColor: brandColor }">{{ providerInitials }}</span>
          </div>
          <div class="model-title-wrap">
            <div class="model-title-row">
              <div class="model-title">{{ config.name }}</div>
              <v-chip
                v-if="isSystemLike"
                size="x-small"
                color="primary"
                variant="flat"
                class="model-flag-chip"
              >
                DEFAULT
              </v-chip>
              <v-chip
                v-else-if="config.programmaticToolCalling"
                size="x-small"
                color="warning"
                variant="tonal"
                class="model-flag-chip"
              >
                TOOLS
              </v-chip>
              <v-chip
                v-if="configuredModelRetired"
                size="x-small"
                color="warning"
                variant="tonal"
                class="model-flag-chip"
              >
                RETIRED MODEL
              </v-chip>
            </div>
            <div class="model-subtitle">{{ config.providerKey }}</div>
          </div>
        </div>

        <div class="model-header-actions">
          <v-tooltip text="Edit Model" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-pencil-outline"
                size="small"
                variant="plain"
                class="model-edit-icon"
                @click.stop="emitEdit"
              />
            </template>
          </v-tooltip>
          <v-tooltip text="Copy Model" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-content-copy"
                size="small"
                variant="plain"
                @click.stop="copyModel"
              />
            </template>
          </v-tooltip>
        </div>
      </div>

      <div
        class="model-spec-grid"
        :class="{ 'model-spec-grid--with-pricing': hasVisiblePricing }"
      >
        <div class="model-spec">
          <div class="model-spec-k">Model</div>
          <div class="model-spec-v model-spec-v-mono">{{ modelChipLabel }}</div>
        </div>
        <div class="model-spec">
          <div class="model-spec-k">Description</div>
          <div class="model-spec-v">{{ summaryDescription }}</div>
        </div>
        <div v-if="inputRate" class="model-spec model-spec--pricing-inline">
          <div class="model-spec-v model-spec-v-pricing-inline">
            <span class="model-spec-pricing-label">Input</span>
            <span class="model-spec-pricing-value">{{ inputLabel }}</span>
          </div>
        </div>
        <div v-if="outputRate" class="model-spec model-spec--pricing-inline">
          <div class="model-spec-v model-spec-v-pricing-inline">
            <span class="model-spec-pricing-label">Output</span>
            <span class="model-spec-pricing-value">{{ outputLabel }}</span>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { ModelConfig, UserModelConfig } from '@/types/models'
import type { ModelPricingLookupResult, ModelPricingRecord } from '@/types/modelPricing'
import { RosettaModel } from '@/types/msq'
import { isRetiredModelId } from '@/utils/models'
import { formatModelPricingAmount, getModelPricingDisplayRates, getModelPricingRecord, normalizeModelPricingLookupModel } from '@/utils/modelPricing'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'

@Options({
  props: {
    model: Object
  },
  computed: {
    ...mapGetters(['modelPricingByProviderModel', 'currentTheme', 'allProviderModels'])
  },
  emits: ['edit']
})
export default class ModelSummaryCard extends Vue {
  model!: ModelConfig | UserModelConfig | null
  modelPricingByProviderModel!: (providerKey: string, model: string) => ModelPricingLookupResult | undefined
  currentTheme!: string
  allProviderModels!: Record<string, RosettaModel[]>
  iconLoadFailed = false

  get config(): ModelConfig {
    if (this.model && 'config' in this.model) {
      return this.model.config
    }
    return (this.model as ModelConfig) || {
      id: '',
      timestamp: 0,
      name: 'Unknown model',
      description: '',
      providerKey: '',
      model: '',
      response: '',
      options: null
    }
  }

  get modelChipLabel(): string {
    return normalizeModelPricingLookupModel(this.config.model || this.config.name)
  }

  get providerInitials(): string {
    const raw = String(this.config.providerKey || '').replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'AI'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.config.providerKey)
  }

  get icon(): ProviderIcon | null {
    return getProviderIcon(this.config.providerKey, this.currentTheme as ThemeName)
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  get pricingLookup(): ModelPricingLookupResult | undefined {
    return this.modelPricingByProviderModel(this.config.providerKey, this.config.model)
  }

  get pricingRecord(): ModelPricingRecord | null {
    return getModelPricingRecord(this.pricingLookup)
  }

  get inputRate() {
    return getModelPricingDisplayRates(this.pricingRecord).input
  }

  get outputRate() {
    return getModelPricingDisplayRates(this.pricingRecord).output
  }

  get hasVisiblePricing(): boolean {
    return !!this.inputRate || !!this.outputRate
  }

  get inputLabel(): string {
    return formatModelPricingAmount(this.inputRate)
  }

  get outputLabel(): string {
    return formatModelPricingAmount(this.outputRate)
  }

  get summaryDescription(): string {
    const text = (this.pricingRecord?.displayName || this.config.description || this.config.name || '').trim()
    return text.length > 0 ? text : 'Model'
  }

  get isSystemLike(): boolean {
    return this.config.name.toLowerCase().includes('default')
  }

  /**
   * Desktop-grid counterpart of ModelCard's "retired model" chip: the saved model id is absent
   * from the provider's discovered list, so generation with it fails. Suppressed while the list
   * is empty/not loaded — see `isRetiredModelId`.
   */
  get configuredModelRetired(): boolean {
    const discovered = this.allProviderModels ? this.allProviderModels[this.config.providerKey] : undefined
    return isRetiredModelId(discovered, this.config.model)
  }

  emitEdit() {
    this.$emit('edit', this.config.id)
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }

  async copyModel() {
    await this.$store.dispatch('addModelCopy', {
      ...this.config,
      id: `${this.config.id}-copy-${Date.now()}`,
      name: `Copy of ${this.config.name}`,
      timestamp: Date.now()
    })
  }
}
</script>

<style scoped lang="scss">
.model-desktop-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.model-shell {
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
}

.model-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.model-header-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.model-provider-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}

.model-provider-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.model-provider-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.model-provider-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}

.model-title-wrap {
  min-width: 0;
}

.model-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.model-title {
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.model-subtitle {
  margin-top: 4px;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.model-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.model-edit-icon {
  opacity: 0;
  transition: opacity 0.18s ease;
}

.model-desktop-card:hover .model-edit-icon,
.model-desktop-card:focus-within .model-edit-icon {
  opacity: 1;
}

.model-flag-chip {
  flex-shrink: 0;
}

.model-spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  padding: 14px 0;
  margin-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.model-spec-grid--with-pricing {
  padding-bottom: 8px;
}

.model-spec-k {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 4px;
}

.model-spec-v {
  font-size: 0.95rem;
  line-height: 1.35;
  font-weight: 600;
}

.model-spec-v-pricing {
  font-size: 0.84rem;
  line-height: 1.2;
  font-weight: 600;
}

.model-spec--pricing-inline {
  align-self: end;
}

.model-spec-v-pricing-inline {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.78rem;
  line-height: 1.1;
}

.model-spec-pricing-label {
  color: rgba(var(--v-theme-on-surface), 0.58);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.68rem;
  font-weight: 500;
}

.model-spec-pricing-value {
  font-size: 0.8rem;
  font-weight: 600;
}

.model-spec-v-mono {
  font-family: var(--font-mono, monospace);
  font-size: 0.86rem;
  word-break: break-word;
}
</style>
